import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  LayoutDashboard,
  FolderOpen,
  Clock,
  CheckCircle,
  AlertTriangle,
  TimerReset,
  Eye,
  Bell,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatMinutes = (m) => {
  if (m === null || m === undefined || m === '' || m <= 0) return '';
  if (m < 60) return `00:${String(Math.round(m)).padStart(2, '0')}`;
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  return `${String(h).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

const KpiCard = ({ icon: Icon, label, value, tone = 'blue', testid }) => (
  <Card className={`bg-gradient-to-br border ${{
    blue: 'from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800',
    amber: 'from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 border-amber-200 dark:border-amber-800',
    emerald: 'from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900 border-emerald-200 dark:border-emerald-800',
    red: 'from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 border-red-200 dark:border-red-800',
    purple: 'from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800',
    slate: 'from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 border-slate-200 dark:border-slate-800',
  }[tone]}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="text-3xl font-bold mt-1" data-testid={testid}>{value}</p>
        </div>
        <Icon className="w-10 h-10 opacity-60" />
      </div>
    </CardContent>
  </Card>
);

export default function ITDashboardPage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const role = employee?.role;
  const canAccess = ['IT Team', 'IT Head', 'Super Admin', 'Management'].includes(role);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/it/dashboard`, { headers: getAuthHeader() });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('IT dashboard fetch', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { if (canAccess) fetchData(); }, [canAccess, fetchData]);

  if (!canAccess) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-muted-foreground">Access denied. IT, Super Admin or Management role required.</p>
        </div>
      </Layout>
    );
  }

  const kpis = data?.kpis || { open: 0, in_progress: 0, completed: 0, escalated: 0, sla_breaches: 0, avg_resolution_minutes: 0, total_tickets: 0 };
  const recent = data?.recent_tickets || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="it-dashboard-page">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600">
            <LayoutDashboard className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">IT Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Operational view —{' '}
              {role === 'IT Team' ? 'tickets assigned to you' : role === 'IT Head' ? 'all IT tickets in your locations' : 'all IT tickets'}
            </p>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {loading ? (
            [...Array(7)].map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
          ) : (
            <>
              <KpiCard icon={FolderOpen} label="Total Tickets" value={kpis.total_tickets} tone="slate" testid="kpi-total-tickets" />
              <KpiCard icon={FolderOpen} label="Open" value={kpis.open} tone="blue" testid="kpi-open" />
              <KpiCard icon={Clock} label="In Progress" value={kpis.in_progress} tone="amber" testid="kpi-in-progress" />
              <KpiCard icon={CheckCircle} label="Completed" value={kpis.completed} tone="emerald" testid="kpi-completed" />
              <KpiCard icon={Bell} label="Escalated" value={kpis.escalated} tone="red" testid="kpi-escalated" />
              <KpiCard icon={AlertTriangle} label="SLA Breaches" value={kpis.sla_breaches} tone="red" testid="kpi-sla-breaches" />
              <KpiCard icon={TimerReset} label="Avg Resolution" value={formatMinutes(kpis.avg_resolution_minutes) || '—'} tone="purple" testid="kpi-avg-resolution" />
            </>
          )}
        </div>

        {/* Recent Tickets Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent IT Tickets</CardTitle>
              <CardDescription>Last 10 operational tickets in your scope</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/it/queue')} data-testid="goto-it-queue">
              View IT Queue
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : recent.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">No IT tickets yet in your scope.</div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket</TableHead>
                      <TableHead>Request Type</TableHead>
                      <TableHead>Requester</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Completion Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.map(t => (
                      <TableRow
                        key={t.ticket_id}
                        data-testid={`it-dash-row-${t.ticket_id}`}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                      >
                        <TableCell>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${t.ticket_id}`); }}
                            className="font-mono text-xs text-primary hover:underline"
                          >
                            {t.ticket_id}
                          </button>
                        </TableCell>
                        <TableCell>{t.request_type}</TableCell>
                        <TableCell>{t.requester_name}</TableCell>
                        <TableCell>{t.ticket_location || '-'}</TableCell>
                        <TableCell className="text-xs">{t.assigned_it_name || <Badge variant="outline">Unassigned</Badge>}</TableCell>
                        <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                        <TableCell><StatusBadge status={t.status} /></TableCell>
                        <TableCell className="text-xs">{formatDate(t.created_at)}</TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`completion-time-${t.ticket_id}`}>
                          {formatMinutes(t.completion_minutes)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
